import clsx from "clsx";

import {
  Avatar,
  Button,
  DragHandleProps,
  EditableText,
  Icon,
} from "@smart/itops-sb-design-system-dom";

import { fieldComponentIcons, fieldComponentNames } from "../../constants";
import { NewFieldOptions } from "../../types";

export type CustomiseFieldRowProps = {
  field: NewFieldOptions;
  index: number;
  dragHandleProps: DragHandleProps;
  isDragging: boolean;
  canReorder: boolean;
  canRemove: boolean;
  onEditLabel: (uri: string, label: string) => void;
  onCommitLabel: (uri: string) => void;
  onRemove: (uri: string) => void;
};

export const CustomiseFieldRow = ({
  field,
  index,
  dragHandleProps,
  isDragging,
  canReorder,
  canRemove,
  onEditLabel,
  onCommitLabel,
  onRemove,
}: CustomiseFieldRowProps) => (
  <div
    className={clsx(
      "flex items-center gap-small p-[0.8rem] rounded bg-white hover:bg-neutral-20 border border-neutral-100",
      isDragging && "opacity-50",
    )}
    data-testid={`customise-field-item-${index}`}
  >
    <div
      {...(canReorder
        ? { ...dragHandleProps.attributes, ...dragHandleProps.listeners }
        : {})}
      aria-label={`reorder ${field.label || "field"}`}
      className={clsx(
        "flex justify-center items-center cursor-grab",
        !canReorder && "invisible pointer-events-none",
      )}
      data-testid="customise-field-drag-handle"
    >
      <Icon name="solidGripDotsVertical" className="w-[0.6rem] px-2" />
    </div>
    <div className="flex justify-between items-center overflow-hidden flex-1">
      <div className="flex items-center gap-small">
        <Avatar
          size={24}
          icon={fieldComponentIcons[field.type].name}
          bgColor={fieldComponentIcons[field.type].bgColor}
        />
        <EditableText
          text={field.label || ""}
          onChange={(next) => onEditLabel(field.uri, next)}
          onComplete={async () => onCommitLabel(field.uri)}
          placeholder="Field label"
          fontWeight="semibold"
        />
      </div>
      <span className="text-caption text-neutral-400 truncate">
        {fieldComponentNames[field.type]}
      </span>
    </div>
    {canRemove && (
      <Button
        aria-label={`remove ${field.label || "field"}`}
        leftIcon="regularTrashCan"
        variant="secondarySubtle"
        size="small"
        dataTestId={`remove-field-${index}`}
        onClick={() => onRemove(field.uri)}
      />
    )}
  </div>
);
